import { useEffect, useId, useState } from 'react';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { completarEndereco, consultarCep, digitosDoCep } from '@/comuns/cep';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { NovaUnidade, UnidadeDaClinica } from '@/data/tipos';
import { avisarQueAsUnidadesMudaram } from '@/session/unidadeAtiva';

export interface UnidadesDaClinicaProps {
    unidades: UnidadeDaClinica[];
    /** A unidade cujo endereço a aba está editando. */
    escolhidaId: string | undefined;
    onEscolher: (id: string) => void;
    /** Criar e arquivar é da gestão (`configuracoes`); o banco recusa de qualquer jeito. */
    podeGerir: boolean;
    onMudou: () => void;
}

const VAZIA: NovaUnidade = {
    nome: '', telefone: '', email: '', cep: '', estado: '', cidade: '', bairro: '', rua: '', numero: '', complemento: '',
};

/**
 * As unidades da clínica — trilha T7.
 *
 * Criar e arquivar moram aqui; o endereço de cada uma se edita no cartão ao lado.
 * Arquivar tem as travas do banco (0101): não sai a última ativa, nem unidade com
 * agendamento futuro — a recusa aparece com o motivo, e nada muda.
 */
export function UnidadesDaClinica({ unidades, escolhidaId, onEscolher, podeGerir, onMudou }: UnidadesDaClinicaProps) {
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const [criando, setCriando] = useState(false);

    const arquivar = async (unidade: UnidadeDaClinica) => {
        const ok = await confirmar({
            titulo: `Arquivar a unidade ${unidade.nome}?`,
            descricao: 'Ela sai do seletor da agenda e dos formulários. Agendamentos antigos continuam mostrando o nome dela.',
            confirmar: 'Arquivar unidade',
        });
        if (!ok) return;
        try {
            await dados().arquivarUnidade(unidade.id);
            aviso({ title: 'Unidade arquivada', description: unidade.nome });
            avisarQueAsUnidadesMudaram();
            onMudou();
        } catch (e) {
            aviso({ title: 'A unidade não foi arquivada', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    return (
        <Card
            title="Unidades"
            subtitle="Cada unidade tem endereço, telefone e salas. A agenda pode ser vista por unidade."
            padding={20}
            actions={podeGerir ? (
                <Button variant="secondary" iconLeft={<Icon name="plus" size={14} />} onClick={() => setCriando(true)}>Nova unidade</Button>
            ) : undefined}
        >
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {unidades.map((u) => {
                    const escolhida = u.id === escolhidaId;
                    return (
                        <li
                            key={u.id}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}
                        >
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 200px', minWidth: 0 }}>
                                <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{u.nome}</span>
                                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                    {[u.rua && `${u.rua}${u.numero ? `, ${u.numero}` : ''}`, u.cidade, u.telefone].filter(Boolean).join(' · ') || 'Sem endereço cadastrado'}
                                </span>
                            </span>
                            {escolhida && unidades.length > 1 && <Badge tone="info">Editando o endereço</Badge>}
                            {!escolhida && unidades.length > 1 && (
                                <Button size="sm" variant="ghost" onClick={() => onEscolher(u.id)}>Editar endereço</Button>
                            )}
                            {podeGerir && unidades.length > 1 && (
                                <Button size="sm" variant="ghost" onClick={() => void arquivar(u)}>Arquivar</Button>
                            )}
                        </li>
                    );
                })}
            </ul>
            {podeGerir && unidades.length === 1 && (
                <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                    A única unidade ativa não se arquiva: a clínica ficaria sem endereço e sem onde agendar.
                </p>
            )}
            {!podeGerir && (
                <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                    Criar e arquivar unidade exige a permissão de configurações da clínica.
                </p>
            )}

            <NovaUnidadeModal
                open={criando}
                onClose={() => setCriando(false)}
                onCriada={(nova) => {
                    avisarQueAsUnidadesMudaram();
                    onMudou();
                    onEscolher(nova.id);
                }}
            />
            {confirmacao}
        </Card>
    );
}

function NovaUnidadeModal({ open, onClose, onCriada }: { open: boolean; onClose: () => void; onCriada: (u: UnidadeDaClinica) => void }) {
    const aviso = useAviso();
    const ids = {
        nome: useId(), telefone: useId(), email: useId(), cep: useId(), estado: useId(),
        cidade: useId(), bairro: useId(), rua: useId(), numero: useId(), complemento: useId(),
    };
    const [form, setForm] = useState<NovaUnidade>(VAZIA);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(VAZIA);
        setErro(null);
    }, [open]);

    if (!open) return null;

    const campo = (chave: keyof NovaUnidade) => (valor: string) => {
        setErro(null);
        setForm((f) => ({ ...f, [chave]: valor }));
    };

    const criar = async (): Promise<boolean> => {
        if (!form.nome.trim()) { setErro('Dê um nome à unidade.'); return false; }
        if (!form.telefone.trim()) { setErro('O telefone da unidade é obrigatório.'); return false; }
        setSalvando(true);
        try {
            const nova = await dados().criarUnidade(form);
            aviso({ title: 'Unidade criada', description: nova.nome });
            onCriada(nova);
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível criar a unidade.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={560} title="Nova unidade"
            description="Endereço e telefone saem nas notificações ao paciente e nos documentos desta unidade."
            dirty={JSON.stringify(form) !== JSON.stringify(VAZIA)} onSave={criar}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void criar()} loading={salvando}>Criar unidade</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(200px,100%),1fr))', gap: 14 }}>
                    <FormField label="Nome" htmlFor={ids.nome} required><Input id={ids.nome} value={form.nome} onChange={campo('nome')} placeholder="Ex.: Moema" /></FormField>
                    <FormField label="Telefone" htmlFor={ids.telefone} required><Input id={ids.telefone} value={form.telefone} onChange={campo('telefone')} /></FormField>
                    <FormField label="E-mail" htmlFor={ids.email}><Input id={ids.email} type="email" value={form.email} onChange={campo('email')} /></FormField>
                    <FormField label="CEP" htmlFor={ids.cep} hint="Ao sair do campo, o endereço vazio é completado pelo CEP.">
                        <Input
                            id={ids.cep} value={form.cep} onChange={campo('cep')}
                            onBlur={() => {
                                const cep = form.cep;
                                if (!digitosDoCep(cep)) return;
                                void consultarCep(cep).then((r) => {
                                    if (r.ok) setForm((f) => completarEndereco(f, r.endereco));
                                    else aviso({ title: 'Endereço não completado', description: r.motivo });
                                });
                            }}
                        />
                    </FormField>
                    <FormField label="Estado" htmlFor={ids.estado}><Input id={ids.estado} value={form.estado} onChange={campo('estado')} /></FormField>
                    <FormField label="Cidade" htmlFor={ids.cidade}><Input id={ids.cidade} value={form.cidade} onChange={campo('cidade')} /></FormField>
                    <FormField label="Bairro" htmlFor={ids.bairro}><Input id={ids.bairro} value={form.bairro} onChange={campo('bairro')} /></FormField>
                    <FormField label="Rua" htmlFor={ids.rua}><Input id={ids.rua} value={form.rua} onChange={campo('rua')} /></FormField>
                    <FormField label="Número" htmlFor={ids.numero}><Input id={ids.numero} value={form.numero} onChange={campo('numero')} /></FormField>
                    <FormField label="Complemento" htmlFor={ids.complemento}><Input id={ids.complemento} value={form.complemento} onChange={campo('complemento')} /></FormField>
                </div>
            </div>
        </Modal>
    );
}
